import LiveCounter from "./LiveCounter";
import { useState, useEffect } from "react";
import axios from "axios";
import ReferralSuccess from "./ReferralSuccess";
import { getStoredReferral } from "../../hooks/useAnalytics";
import { useI18n } from "@/i18n/I18nProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESSI = [
    { value: "gastronomia", tKey: "wl.interest.gastronomia" },
    { value: "natura", tKey: "wl.interest.natura" },
    { value: "cultura", tKey: "wl.interest.cultura" },
    { value: "esperienze", tKey: "wl.interest.esperienze" },
    { value: "borghi", tKey: "wl.interest.borghi" },
    { value: "mare", tKey: "wl.interest.mare" },
];

const initial = {
    nome: "",
    email: "",
    citta: "",
    interessi: [],
    consenso_privacy: false,
};

export default function WaitlistForm() {
    const { t, lang } = useI18n();
    const [form, setForm] = useState(initial);
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");
    const [successEntry, setSuccessEntry] = useState(null);
    const [referredBy, setReferredBy] = useState(null);
    const [inviterName, setInviterName] = useState("");

    useEffect(() => {
        const stored = getStoredReferral();
        if (!stored) return;
        setReferredBy(stored);
        axios
            .get(`${BACKEND_URL}/api/waitlist/referral/${stored}`)
            .then(({ data }) => {
                if (data?.nome) setInviterName(data.nome);
            })
            .catch(() => {
                setReferredBy(null);
                setInviterName("");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // mount-only by design: the referral code comes from localStorage once per page load

    const toggleInterest = (val) => {
        setForm((f) => ({
            ...f,
            interessi: f.interessi.includes(val)
                ? f.interessi.filter((i) => i !== val)
                : [...f.interessi, val],
        }));
    };

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg("");
        if (!form.consenso_privacy) {
            setErrorMsg(t("wl.errorConsent"));
            return;
        }
        setStatus("loading");
        try {
            const payload = { ...form };
            if (referredBy) payload.referred_by = referredBy;
            const { data } = await axios.post(`${API}/waitlist`, payload);
            setSuccessEntry(data);
            setStatus("success");
        } catch (err) {
            setStatus("error");
            const detail =
                err?.response?.data?.detail ||
                t("wl.errorGeneric");
            setErrorMsg(
                typeof detail === "string" ? detail : t("wl.errorRequest"),
            );
        }
    };

    if (status === "success" && successEntry) {
        return <ReferralSuccess entry={successEntry} />;
    }

    return (
        <section
            id="waitlist"
            data-testid="waitlist-section"
            style={{
                background: "var(--itly-teal-900)",
                color: "var(--itly-on-teal)",
                padding: "104px 32px",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Ambient orange glow */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: -160,
                    right: -160,
                    width: 460,
                    height: 460,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(232,113,76,0.18) 0%, rgba(232,113,76,0) 70%)",
                }}
            />

            <div
                style={{
                    position: "relative",
                    maxWidth: 900,
                    margin: "0 auto",
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
                    gap: 56,
                    alignItems: "center",
                }}
                className="itly-waitlist-grid"
            >
                <WaitlistIntro t={t} />

                <WaitlistFormBody
                    t={t}
                    form={form}
                    status={status}
                    errorMsg={errorMsg}
                    referredBy={referredBy}
                    inviterName={inviterName}
                    onChange={onChange}
                    onSubmit={onSubmit}
                    toggleInterest={toggleInterest}
                />
            </div>
        </section>
    );
}

function WaitlistIntro({ t }) {
    return (
        <div>
            <p
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12.5,
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "var(--itly-orange-500)",
                    margin: 0,
                }}
            >
                {t("wl.badge")}
            </p>
            <h2
                className="itly-serif"
                style={{
                    marginTop: 14,
                    marginBottom: 0,
                    fontSize: "clamp(38px, 4.4vw, 60px)",
                    lineHeight: 1.03,
                    color: "var(--itly-on-teal)",
                    letterSpacing: "-0.022em",
                }}
            >
                {t("wl.h2_a")}{" "}
                <em
                    style={{
                        fontStyle: "italic",
                        color: "var(--itly-orange-500)",
                        fontWeight: 500,
                    }}
                >
                    {t("wl.h2_b")}
                </em>
            </h2>
            <p
                style={{
                    marginTop: 20,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 16.5,
                    lineHeight: 1.6,
                    color: "rgba(247, 244, 238, 0.72)",
                    maxWidth: 400,
                }}
            >
                {t("wl.sub")}
            </p>
            <div style={{ marginTop: 22 }}>
                <LiveCounter variant="dark" testid="waitlist-live-counter" />
            </div>
        </div>
    );
}

function WaitlistFormBody({ t, form, status, errorMsg, referredBy, inviterName, onChange, onSubmit, toggleInterest }) {
    return (
        <form
            onSubmit={onSubmit}
            data-testid="waitlist-form"
            style={{
                background: "rgba(247, 244, 238, 0.06)",
                border: "1px solid rgba(247, 244, 238, 0.14)",
                borderRadius: 20,
                padding: "30px 28px",
            }}
            noValidate
        >
            {referredBy && (
                <div
                    data-testid="referred-banner"
                    style={{
                        marginBottom: 18,
                        padding: "10px 14px",
                        borderRadius: 12,
                        background: "rgba(232, 113, 76, 0.14)",
                        border: "1px solid rgba(232, 113, 76, 0.35)",
                        color: "#FBD5C6",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 13.5,
                        lineHeight: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <span aria-hidden="true" style={{ fontSize: 16, color: "var(--itly-orange-500)" }}>✦</span>
                    <span>
                        {inviterName
                            ? <>{t("wl.invitedBy")} <strong style={{ color: "var(--itly-on-teal)" }}>{inviterName}</strong></>
                            : <>{t("wl.invitedByCode")} <strong style={{ color: "var(--itly-on-teal)", fontFamily: "'JetBrains Mono', monospace" }}>{referredBy}</strong></>}
                    </span>
                </div>
            )}

            <DarkField label={t("wl.nome")}>
                <input
                    name="nome"
                    value={form.nome}
                    onChange={onChange}
                    required
                    className="itly-input-dark"
                    data-testid="waitlist-nome"
                    placeholder={t("wl.placeholderName")}
                    autoComplete="given-name"
                />
            </DarkField>

            <DarkField label={t("wl.email")}>
                <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    className="itly-input-dark"
                    data-testid="waitlist-email"
                    placeholder={t("wl.placeholderEmail")}
                    autoComplete="email"
                />
            </DarkField>

            <DarkField label={t("wl.citta")}>
                <input
                    name="citta"
                    value={form.citta}
                    onChange={onChange}
                    className="itly-input-dark"
                    data-testid="waitlist-citta"
                    placeholder={t("wl.placeholderCity")}
                    autoComplete="address-level2"
                />
            </DarkField>

            <div style={{ marginTop: 22 }}>
                <span
                    style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--itly-on-teal)",
                        letterSpacing: "0.01em",
                    }}
                >
                    {t("wl.interestsQuestion")}
                </span>
                <div
                    style={{
                        marginTop: 10,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                    }}
                    data-testid="waitlist-interessi"
                >
                    {INTERESSI.map((i) => {
                        const on = form.interessi.includes(i.value);
                        return (
                            <button
                                key={i.value}
                                type="button"
                                onClick={() => toggleInterest(i.value)}
                                data-testid={`interest-${i.value}`}
                                className={`itly-chip ${on ? "on" : ""}`}
                            >
                                {on && (
                                    <span
                                        aria-hidden="true"
                                        style={{ marginRight: 6 }}
                                    >
                                        ✦
                                    </span>
                                )}
                                {t(i.tKey)}
                            </button>
                        );
                    })}
                </div>
            </div>

            <label
                style={{
                    marginTop: 24,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    color: "rgba(247, 244, 238, 0.72)",
                    lineHeight: 1.5,
                    cursor: "pointer",
                }}
            >
                <input
                    type="checkbox"
                    name="consenso_privacy"
                    checked={form.consenso_privacy}
                    onChange={onChange}
                    required
                    data-testid="waitlist-consenso"
                    style={{
                        marginTop: 3,
                        accentColor: "var(--itly-orange-500)",
                        cursor: "pointer",
                    }}
                />
                <PrivacyConsentLabel t={t} />
            </label>

            {errorMsg && (
                <div
                    data-testid="waitlist-error"
                    style={{
                        marginTop: 16,
                        padding: "10px 14px",
                        borderRadius: 12,
                        background: "rgba(232, 113, 76, 0.15)",
                        border: "1px solid rgba(232, 113, 76, 0.35)",
                        color: "#FBD5C6",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 13.5,
                    }}
                >
                    {errorMsg}
                </div>
            )}

            <button
                type="submit"
                disabled={status === "loading"}
                data-testid="waitlist-submit"
                className="itly-btn-primary"
                style={{
                    marginTop: 24,
                    width: "100%",
                    justifyContent: "center",
                    background: "var(--itly-orange-500)",
                    color: "#fff",
                }}
            >
                {status === "loading"
                    ? t("wl.submitting")
                    : t("wl.submit")}
                {status !== "loading" && (
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                    </svg>
                )}
            </button>
        </form>
    );
}

function DarkField({ label, children }) {
    return (
        <label
            style={{
                display: "block",
                marginTop: 14,
            }}
        >
            <span
                style={{
                    display: "block",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--itly-on-teal)",
                    marginBottom: 8,
                    letterSpacing: "0.005em",
                }}
            >
                {label}
            </span>
            {children}
        </label>
    );
}

function PrivacyConsentLabel({ t }) {
    const blurb = t("wl.privacyBlurb");
    const linkText = t("wl.privacyLink");
    const [before, after = ""] = blurb.split(linkText);
    return (
        <span>
            {before}
            <a
                href="/privacy"
                style={{
                    color: "var(--itly-on-teal)",
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                }}
            >
                {linkText}
            </a>
            {after}
        </span>
    );
}
